import { EffectiveDefinition, Value } from '../../models';
import { Calculation } from '../../models/EffectiveDefinition';
import { TransformCreator, TransformFunction } from '../transform';
import { applySimplePredicate, SimplePredicateCalculation } from './SimplePredicateCalculation';

export const IfElseType = 'if-else';

/**
 * IfElse
 *
 * This calculation is essentially an if/else tree.
 * Branches in the `Branches` array will be evaluated in order, and the
 * first one that matches the row (returns `true`) will have its `result`
 * written back to the row.
 *
 * If a `Branch` has no associated `predicate`, it will be considered to
 * always be `true`.
 *
 * If no `Branch` matches the row, no result will be written back to the row.
 */
export interface IfElseCalculation {
  readonly type: typeof IfElseType;
  readonly branches: ReadonlyArray<Branch>;
}

export interface Branch {
  readonly predicate?: SimplePredicateCalculation;
  readonly result: Value;
}

export const isIfElseCalculation = (calc?: Calculation): calc is IfElseCalculation => {
  return calc !== undefined && calc.type === IfElseType;
};

export const ifElseTransformCreator: TransformCreator = (
  definition: EffectiveDefinition,
  name: string
): TransformFunction => {
  const column = definition.schema[name];
  if (column.calculation === undefined) {
    throw new Error(`Could not find calculated column with name: ${name}`);
  }
  const calc = column.calculation;
  if (!isIfElseCalculation(calc)) {
    throw new Error(`Could not find if-else column with name: ${name}`);
  }
  return (sourceTable, windowedTable) => {
    return windowedTable.map((row) => {
      const result = calc.branches.find((value) => {
        if (value.predicate === undefined) {
          return value;
        }
        return applySimplePredicate(value.predicate, row);
      });

      return { ...row, [name]: result?.result };
    });
  };
};
